import { useMemo } from 'react';
import { useCostTracking } from '../../contexts/CostTrackingContext';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { resolveUnitCategoryName } from '../pages-equipment/equipmentFieldUtils';
import { Btn, Card, DataTable, type DataTableColumn } from '../ui';
import type { SingleUnit } from '../../types/schema';

interface CostTrackingEquipmentListProps {
    onOpenUnit: (unitId: string) => void;
}

const formatCurrency = (value: number) =>
    value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export const CostTrackingEquipmentList = ({ onOpenUnit }: CostTrackingEquipmentListProps) => {
    const { companyUnits, companyGroupedEquipmentData } = useEquipmentContext();
    const { expenseRecords, getTotalCostForUnit, isLoading } = useCostTracking();

    const enrolledUnits = useMemo(() => {
        const withCosts = new Set(
            expenseRecords.map((record) => record.unitId).filter((id): id is string => Boolean(id)),
        );
        return companyUnits
            .filter(
                (unit) =>
                    unit.equipmentStatus !== false &&
                    (unit.costTrackingModuleEnrollment || withCosts.has(unit.id)),
            )
            .sort((left, right) => (left.name || '').localeCompare(right.name || ''));
    }, [companyUnits, expenseRecords]);

    const columns = useMemo<DataTableColumn<SingleUnit>[]>(
        () => [
            {
                id: 'name',
                header: 'Equipment',
                mobileCardPrimary: true,
                renderCell: (unit) => unit.name || 'Untitled unit',
            },
            {
                id: 'category',
                header: 'Category',
                renderCell: (unit) => resolveUnitCategoryName(unit, companyGroupedEquipmentData),
            },
            {
                id: 'total',
                header: 'Recorded cost',
                align: 'right',
                renderCell: (unit) => formatCurrency(getTotalCostForUnit(unit.id)),
            },
            {
                id: 'actions',
                header: 'Actions',
                headerLabel: 'Actions',
                align: 'center',
                mobileCardAction: true,
                renderCell: (unit) => (
                    <Btn
                        variant="link"
                        buttonSize="sm"
                        buttonText="Open"
                        ariaLabel={`Open cost detail for ${unit.name || 'unit'}`}
                        buttonOnClick={() => onOpenUnit(unit.id)}
                    />
                ),
            },
        ],
        [companyGroupedEquipmentData, getTotalCostForUnit, onOpenUnit],
    );

    return (
        <Card variant="card-lg" className="ct-list-card">
            <div className="ct-equipment-list-head">
                <h2>Equipment</h2>
                <p>Units enrolled in Cost Tracking, and units that already have recorded costs.</p>
            </div>
            {isLoading ? (
                <p className="ct-unit-empty-msg">Loading cost data...</p>
            ) : (
                <DataTable
                    ariaLabel="Equipment enrolled in Cost Tracking"
                    columns={columns}
                    rows={enrolledUnits}
                    getRowKey={(unit) => unit.id}
                    mobileCardLayout
                    emptyState={<p className="ct-unit-empty-msg">No equipment is enrolled in Cost Tracking.</p>}
                />
            )}
        </Card>
    );
};
